// ════════════════════════════════════════════════════════════════════
// LYRAEA — audio.js   ·   THE ENGINE
// ────────────────────────────────────────────────────────────────────
// ONE Audio element, created once at module scope, living OUTSIDE React.
// This is the single most important decision in the app and it is not
// stylistic: an <audio> element rendered by a component is destroyed and
// recreated whenever that component unmounts or its key changes, and the
// music stops mid-bar. A module-level element is owned by the page, not
// by the tree — screens can mount, unmount, and re-render freely while
// playback continues untouched. That is what "keeps playing while you
// walk around another game" actually requires.
//
// React subscribes to it, never the other way around: a tiny store with
// a subscriber set, and one useAudio() hook. No context provider, no
// prop drilling, no re-render storm — components read exactly the slice
// they need and the engine does not care whether anyone is listening.
//
// ── LOCK-SCREEN PLAYBACK ─────────────────────────────────────────────
// MediaSession is what puts the title, artist and cover art on a phone's
// lock screen and wires the hardware/notification buttons to this queue.
// Without it a locked phone shows "lyraea.pages.dev" and the buttons do
// nothing. It is a dozen lines and it is the difference between a web
// page that plays sound and something that behaves like a music app.
//
// ── POSITION UPDATES ─────────────────────────────────────────────────
// Driven by the element's own `timeupdate` (≈4/sec), NOT by an interval
// or rAF. An interval keeps firing when the tab is backgrounded and the
// phone is asleep, burning battery to update a progress bar nobody can
// see; `timeupdate` is throttled by the browser exactly when it should be.
// ════════════════════════════════════════════════════════════════════

import { useEffect, useState } from "react";
import { resolveTrack, resolveCover } from "./CDN";
import { chapterAt, loadBookState, saveBookState } from "./book";

const hasDOM = typeof window !== "undefined";

// ── The element ──────────────────────────────────────────────────────
const el = hasDOM ? new Audio() : null;
if (el) {
  el.preload = "metadata";
  // Debug handle. The element is never in the DOM (that is the point), so
  // without this there is no way to inspect it from a console or a browser
  // automation tool — you end up guessing at readyState and networkState
  // during exactly the bugs that are hardest to reason about.
  window.__lyAudio = el;
  // NOTE: crossOrigin is deliberately NOT set. Setting it to "anonymous"
  // puts the media fetch into CORS mode, and the moment the asset host
  // does not return Access-Control-Allow-Origin the load stalls silently —
  // networkState LOADING, readyState HAVE_NOTHING, no error event, forever.
  // Plain playback of cross-origin audio needs no CORS at all. Only set
  // this if a visualiser is added AND the CDN is sending CORS headers,
  // and re-test playback the same day if you do.
}

// ── The store ────────────────────────────────────────────────────────
let state = {
  queue: [],          // Track[]
  index: -1,          // position in queue
  track: null,        // convenience: queue[index]
  playing: false,
  loading: false,
  position: 0,
  duration: 0,
  volume: 1,
  muted: false,
  shuffle: false,
  repeat: "off",      // "off" | "all" | "one"
  sleepAt: null,      // ms timestamp the sleep timer fires, or null
  error: null,
  rate: 1,            // playback speed — audiobooks only; music always plays at 1×
  chapter: 0,         // audiobook chapter index at `position` (see book.js)
};

// An audiobook is a Track carrying a `book` (chapters + word timing),
// opened from a dropped file. See book.js for why it never touches the CDN.
const isBook = (t) => !!(t && t.book);

const subs = new Set();
const emit = () => { const s = state; subs.forEach((f) => f(s)); };
const setState = (patch) => { state = { ...state, ...patch }; emit(); };

export const getAudioState = () => state;

// ── Play reporting ───────────────────────────────────────────────────
// The engine does not import Firebase. It calls back to whatever the app
// registered, so audio.js stays a pure media module and can be lifted into
// any other IE title that wants a player without dragging a backend along.
let reportPlay = null;
export const setPlayReporter = (fn) => { reportPlay = fn; };
let countedFor = null;   // trackId whose play has already been reported

// A seek that is waiting for the file to admit how long it is.
// Assigning `el.currentTime` before metadata has loaded is SILENTLY
// IGNORED by every browser — the element has no timeline to seek within
// yet, so the write evaporates and playback starts from zero. This is
// what makes a naive "resume where you left off" quietly never resume.
// The position is parked here and applied on loadedmetadata instead.
let pendingSeek = 0;

// ── Persistence ──────────────────────────────────────────────────────
// Remember what was playing, and where, across a reload. Never auto-plays
// on restore — browsers forbid it and it would be rude besides. It cues up
// silently and waits for a press.
const SAVE_KEY = "lyraea.session.v1";
const save = () => {
  if (!hasDOM) return;
  // A dropped book is a blob: URL, dead after reload. Its place is kept by
  // saveBook() under the file's identity instead, and the last CATALOGUE
  // session is left intact rather than overwritten with a dead link.
  if (state.track?.local) { saveBook(); return; }
  try {
    localStorage.setItem(SAVE_KEY, JSON.stringify({
      queue: state.queue, index: state.index,
      position: Math.floor(state.position),
      shuffle: state.shuffle, repeat: state.repeat,
      volume: state.volume,
    }));
  } catch { /* quota or private mode — losing the session is not an error */ }
};

let lastBookSave = 0;
function saveBook() {
  const t = state.track;
  if (!isBook(t) || !el) return;
  lastBookSave = Date.now();
  saveBookState(t.id, { position: Math.floor(el.currentTime || 0), rate: state.rate });
}

export const restoreSession = () => {
  if (!hasDOM || !el) return;
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return;
    const s = JSON.parse(raw);
    if (!s?.queue?.length || s.index < 0) return;
    const track = s.queue[s.index];
    if (!track) return;
    state = { ...state, queue: s.queue, index: s.index, track,
              shuffle: !!s.shuffle, repeat: s.repeat || "off",
              volume: typeof s.volume === "number" ? s.volume : 1 };
    el.volume = state.volume;
    el.src = resolveTrack(track.src);
    pendingSeek = s.position || 0;          // applied on loadedmetadata
    state = { ...state, position: pendingSeek };
    countedFor = track.id;   // do not re-count a play we already counted
    setMediaMetadata(track);
    emit();
  } catch { /* ignore a corrupt blob */ }
};

// ── MediaSession ─────────────────────────────────────────────────────
function setMediaMetadata(track) {
  if (!hasDOM || !navigator.mediaSession || !track) return;
  try {
    const art = resolveCover(track.cover);
    // A book shows the CHAPTER as the title on the lock screen and the
    // book as the album — the way every audiobook app does it.
    const ch = isBook(track) ? track.book.chapters[state.chapter] : null;
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: ch ? ch.title : (track.title || "Untitled"),
      artist: track.artist || "Unknown",
      album: ch ? track.title : (track.albumTitle || "Lyraea"),
      artwork: art ? [
        { src: art, sizes: "512x512", type: "image/webp" },
        { src: art, sizes: "256x256", type: "image/webp" },
      ] : [],
    });
  } catch { /* older browsers: metadata is optional, playback is not */ }
}

function wireMediaSession() {
  if (!hasDOM || !navigator.mediaSession) return;
  const set = (action, fn) => {
    try { navigator.mediaSession.setActionHandler(action, fn); } catch { /* unsupported action */ }
  };
  set("play",  () => play());
  set("pause", () => pause());
  // In a book, the lock screen's skip buttons move by CHAPTER, and the
  // seek buttons by 15 s — not by track, which would leave the book.
  set("previoustrack", () => (isBook(state.track) ? prevChapter() : prev()));
  set("nexttrack", () => (isBook(state.track) ? nextChapter() : next("user")));
  const step = (d) => d?.seekOffset || (isBook(state.track) ? 15 : 10);
  set("seekbackward", (d) => seek(Math.max(0, el.currentTime - step(d))));
  set("seekforward",  (d) => seek(Math.min(el.duration || 0, el.currentTime + step(d))));
  set("seekto", (d) => { if (d?.seekTime != null) seek(d.seekTime); });
  set("stop", () => { pause(); seek(0); });
  // Losing the last few seconds of a book to a swiped-away tab is the one
  // case the 5 s save cannot cover on its own.
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") saveBook(); });
}

// ── Element events ───────────────────────────────────────────────────
if (el) {
  // Duration is read on THREE events, not one, and that redundancy is the
  // fix for a real bug: `loadedmetadata` can fire while `el.duration` is
  // still NaN, and `NaN || 0` silently becomes 0 — permanently, because
  // nothing ever asked again. The symptom is a track that plays perfectly
  // with a scrubber pinned at the far left and a 0:00 length. So:
  // loadedmetadata (usually), durationchange (when it firms up), and a
  // cheap re-check on timeupdate (the belt-and-braces catch).
  const syncDuration = () => {
    const d = el.duration;
    if (isFinite(d) && d > 0 && d !== state.duration) setState({ duration: d });
  };
  el.addEventListener("loadedmetadata", () => {
    syncDuration();
    // Now — and only now — is there a timeline to seek within.
    if (pendingSeek > 0 && isFinite(el.duration) && pendingSeek < el.duration - 1) {
      el.currentTime = pendingSeek;
    }
    pendingSeek = 0;
    setState({ loading: false, position: el.currentTime || 0 });
  });
  el.addEventListener("durationchange", syncDuration);

  el.addEventListener("timeupdate", () => {
    const pos = el.currentTime || 0;
    syncDuration();
    // A play is counted once, at 20 seconds or the halfway mark —
    // whichever comes first — so a skipped track never counts and a
    // 90-second interlude is not held to the same bar as a 6-minute one.
    // A dropped book is not in the catalogue — counting it would write a
    // play for an id no track has.
    if (reportPlay && state.track && !state.track.local && countedFor !== state.track.id) {
      const bar = Math.min(20, (el.duration || 40) / 2);
      if (pos >= bar) { countedFor = state.track.id; reportPlay(state.track.id); }
    }
    if (isBook(state.track)) {
      const ch = chapterAt(state.track.book, pos);
      if (ch !== state.chapter) { state = { ...state, chapter: ch }; setMediaMetadata(state.track); saveBook(); }
      else if (Date.now() - lastBookSave > 5000) saveBook();
    }
    setState({ position: pos });
    if (navigator.mediaSession?.setPositionState && el.duration) {
      try {
        navigator.mediaSession.setPositionState({
          duration: el.duration, position: pos, playbackRate: el.playbackRate,
        });
      } catch { /* Safari throws on odd values mid-seek; cosmetic only */ }
    }
  });

  el.addEventListener("play",  () => setState({ playing: true, error: null }));
  el.addEventListener("pause", () => { setState({ playing: false }); save(); });
  el.addEventListener("waiting", () => setState({ loading: true }));
  el.addEventListener("playing", () => setState({ loading: false }));
  el.addEventListener("ended",  () => next("auto"));

  el.addEventListener("error", () => {
    // The commonest cause by far is an asset URL that resolved to the APP
    // domain and came back as HTML — see the trap at the top of CDN.js.
    // Say so in the message rather than making someone rediscover it.
    setState({
      loading: false, playing: false,
      error: "This track would not load. Check the asset URL is on lyraea-assets, not the app domain.",
    });
  });

  wireMediaSession();
}

// ════════════════════════════════════════════════════════════════════
// FORMAT CHECK — catch an undecodable file BEFORE it fails silently
// ════════════════════════════════════════════════════════════════════
// The element's `error` event cannot tell "wrong codec" apart from "bad
// URL" — both arrive as MEDIA_ERR_SRC_NOT_SUPPORTED with no detail. That
// ambiguity is expensive: the honest fix for one is nothing like the fix
// for the other. So the extension is checked up front and the two cases
// get two different sentences.
//
// .wma is the one that will actually bite: Windows hands it out by
// default, it looks like a normal audio file, and NO browser on earth
// decodes it — it is a Microsoft codec that never shipped in the web
// platform. Same story for .wav in practice (playable, but 10× the size
// of an mp3 for no audible gain over a phone connection).
const PLAYABLE = {
  mp3: "audio/mpeg", m4a: "audio/mp4", m4b: "audio/mp4", aac: "audio/aac",
  ogg: "audio/ogg", opus: 'audio/ogg; codecs="opus"',
  webm: "audio/webm", wav: "audio/wav", flac: "audio/flac",
};

const DEAD = {
  wma: "Windows Media (.wma) — no browser decodes it.",
  aiff: "AIFF — Safari only, and enormous.",
  aif: "AIFF — Safari only, and enormous.",
  m4p: "Protected iTunes audio (.m4p) — DRM, cannot be played here.",
  wpl: "That is a Windows playlist file, not audio.",
};

export function formatProblem(src) {
  const ext = String(src || "").split("?")[0].split(".").pop().toLowerCase();
  if (DEAD[ext]) return `${DEAD[ext]} Convert it to MP3 and re-upload.`;
  const mime = PLAYABLE[ext];
  if (!mime) return `".${ext}" is not an audio format the web plays. Use MP3.`;
  // canPlayType returns "", "maybe" or "probably" — only "" is a real no.
  if (el && el.canPlayType(mime) === "") {
    return `This browser cannot play .${ext} files. MP3 works everywhere.`;
  }
  return null;
}

// ════════════════════════════════════════════════════════════════════
// CONTROLS
// ════════════════════════════════════════════════════════════════════

// loadQueue — the only way audio starts. `startIndex` is a position in
// the list as given; shuffle reorders playback ORDER, never this list, so
// the queue a listener sees always matches the queue they picked.
export function loadQueue(tracks, startIndex = 0, autoplay = true) {
  if (!el || !tracks?.length) return;
  const index = Math.max(0, Math.min(startIndex, tracks.length - 1));
  const track = tracks[index];

  // Refuse an undecodable file loudly and early rather than letting the
  // element fail with a message that blames the URL.
  // A dropped file's src is a blob: URL with no extension; judge its name.
  const problem = formatProblem(track.local ? track.fileName : track.src);
  if (problem) {
    setState({ queue: tracks, index, track, playing: false, loading: false, error: problem });
    return;
  }

  if (state.track && state.track.id !== track.id) saveBook();   // leaving a book: keep its place
  const saved = isBook(track) ? loadBookState(track.id) : {};
  const rate = isBook(track) ? (saved.rate || 1) : 1;
  setState({ queue: tracks, index, track, error: null, loading: true,
             position: saved.position || 0, rate,
             chapter: isBook(track) ? chapterAt(track.book, saved.position || 0) : 0 });
  el.src = resolveTrack(track.src);
  // playbackRate resets on every load; defaultPlaybackRate is what sticks.
  el.defaultPlaybackRate = rate; el.playbackRate = rate;
  // A book resumes where it was left; a deliberate new song starts at zero.
  pendingSeek = saved.position || 0;
  countedFor = null;
  setMediaMetadata(track);
  save();
  if (autoplay) play();
}

export function play() {
  if (!el || !state.track) return;
  // The promise rejects when a browser blocks autoplay. That is not a
  // failure worth surfacing as an error — it means "a human has to press
  // something first", which the UI already offers.
  const p = el.play();
  if (p?.catch) p.catch(() => setState({ playing: false }));
  if (navigator.mediaSession) navigator.mediaSession.playbackState = "playing";
}

export function pause() {
  if (!el) return;
  el.pause();
  if (navigator.mediaSession) navigator.mediaSession.playbackState = "paused";
}

export function toggle() { state.playing ? pause() : play(); }

export function seek(sec) {
  if (!el || !isFinite(sec)) return;
  el.currentTime = Math.max(0, Math.min(sec, el.duration || sec));
  setState({ position: el.currentTime });
}

// ── Audiobook controls ──────────────────────────────────────────────
export function setRate(r) {
  if (!el) return;
  const rate = Math.max(0.5, Math.min(3, r));
  el.defaultPlaybackRate = rate; el.playbackRate = rate;
  setState({ rate });
  saveBook();
}

export function seekChapter(i) {
  const t = state.track;
  if (!isBook(t)) return;
  const cs = t.book.chapters;
  const c = cs[Math.max(0, Math.min(i, cs.length - 1))];
  seek(c.start + 0.01);
}
export function nextChapter() {
  if (!isBook(state.track)) return;
  // Last chapter: nowhere to go. (Calling next() here would reload the
  // same one-item queue and bounce the listener back to a saved spot.)
  if (state.chapter >= state.track.book.chapters.length - 1) return;
  seekChapter(state.chapter + 1);
}
// Like "previous track": a few seconds in, it restarts THIS chapter.
export function prevChapter() {
  const t = state.track;
  if (!isBook(t)) return;
  const here = t.book.chapters[state.chapter];
  if (el && el.currentTime - here.start > 3) seekChapter(state.chapter);
  else seekChapter(state.chapter - 1);
}

function pickIndex(dir, reason) {
  const n = state.queue.length;
  if (!n) return -1;
  if (state.repeat === "one" && reason === "auto") return state.index;
  if (state.shuffle) {
    if (n === 1) return state.repeat === "off" && reason === "auto" ? -1 : 0;
    let i = state.index;
    // Never hand back the track that just played — a shuffle that repeats
    // itself immediately reads as a bug even though it is honest randomness.
    while (i === state.index) i = Math.floor(Math.random() * n);
    return i;
  }
  const nextI = state.index + dir;
  if (nextI >= n) return state.repeat === "all" ? 0 : (reason === "auto" ? -1 : n - 1);
  if (nextI < 0) return state.repeat === "all" ? n - 1 : 0;
  return nextI;
}

export function next(reason = "user") {
  const i = pickIndex(1, reason);
  if (i < 0) { pause(); seek(0); return; }        // end of the line
  if (i === state.index && state.repeat === "one") { seek(0); play(); return; }
  loadQueue(state.queue, i, true);
}

export function prev() {
  // Standard player behaviour: past three seconds, "previous" restarts
  // the current track rather than leaving it.
  if (el && el.currentTime > 3) { seek(0); return; }
  const i = pickIndex(-1, "user");
  loadQueue(state.queue, Math.max(0, i), true);
}

// Jump straight to a position in the queue already loaded — what tapping
// a row in the queue view does. Reuses loadQueue so metadata, play
// counting and persistence all follow the same single path.
export function jumpTo(i) {
  if (i < 0 || i >= state.queue.length || i === state.index) return;
  loadQueue(state.queue, i, true);
}

export function playTrack(track, queue) {
  const list = queue?.length ? queue : [track];
  const idx = Math.max(0, list.findIndex((t) => t.id === track.id));
  loadQueue(list, idx, true);
}

// ════════════════════════════════════════════════════════════════════
// QUEUE EDITING  —  add · play next · remove · reorder
// ────────────────────────────────────────────────────────────────────
// ⚠ NONE OF THESE MAY GO THROUGH loadQueue(). That is the whole trap in
//   this block. loadQueue tears the element down and re-points `src`,
//   which restarts the current track from zero — so "add this to the
//   end of the queue" would interrupt the song you are listening to,
//   which is the one thing a queue must never do.
//
//   So: splice the array, fix up `index` so it still points at the SAME
//   TRACK OBJECT it pointed at before, and emit. The element is never
//   touched. Playback does not know anything happened.
//
// The index fix-up is the fiddly part and it is why these are four
// functions in the engine rather than four array spreads at four call
// sites — getting it wrong desynchronises the highlighted row from the
// audible track, and that bug is invisible until somebody notices the
// wrong title in the bar.

const _commitQueue = (queue, index) => {
  const i = Math.max(-1, Math.min(index, queue.length - 1));
  setState({ queue, index: i, track: queue[i] || null });
  save();
};

// Append to the end. Returns the new length so a caller can toast it.
export function addToQueue(track) {
  if (!track) return state.queue.length;
  const queue = [...state.queue, track];
  _commitQueue(queue, state.index);
  return queue.length;
}

// Insert directly after the playing track. If nothing is cued this
// behaves like addToQueue rather than doing nothing — "play next" with
// an empty queue obviously means "play this".
export function playNext(track) {
  if (!track) return;
  if (state.index < 0 || !state.queue.length) {
    loadQueue([track], 0, true);
    return;
  }
  const queue = [...state.queue];
  queue.splice(state.index + 1, 0, track);
  _commitQueue(queue, state.index);
}

// Remove one row. Removing the PLAYING row is the interesting case: the
// track is already loaded in the element and still audible, so we leave
// it playing and let it finish — the queue simply no longer contains a
// next-after-it. Yanking the audio out from under someone because they
// tidied a list is a worse answer than a song finishing.
export function removeAt(i) {
  if (i < 0 || i >= state.queue.length) return;
  const queue = state.queue.filter((_, n) => n !== i);
  let index = state.index;
  if (i < state.index) index -= 1;
  else if (i === state.index) index = Math.min(state.index, queue.length - 1);
  _commitQueue(queue, index);
}

// Move a row. Used by drag-to-reorder and by the up/down buttons, which
// exist because drag on a touch screen inside a scrolling list is a
// coin toss and a reorder that fails silently is worse than a button.
export function moveInQueue(from, to) {
  const n = state.queue.length;
  if (from < 0 || from >= n || to < 0 || to >= n || from === to) return;
  const queue = [...state.queue];
  const [row] = queue.splice(from, 1);
  queue.splice(to, 0, row);

  // Follow the track that was playing, wherever it ended up.
  const playing = state.queue[state.index];
  const index = playing ? queue.findIndex((t) => t === playing) : state.index;
  _commitQueue(queue, index);
}

export function setVolume(v) {
  if (!el) return;
  const vol = Math.max(0, Math.min(1, v));
  el.volume = vol;
  el.muted = vol === 0;
  setState({ volume: vol, muted: el.muted });
  save();
}

export function toggleShuffle() { setState({ shuffle: !state.shuffle }); save(); }

export function cycleRepeat() {
  const order = ["off", "all", "one"];
  setState({ repeat: order[(order.indexOf(state.repeat) + 1) % 3] });
  save();
}

// ════════════════════════════════════════════════════════════════════
// SLEEP TIMER
// ════════════════════════════════════════════════════════════════════
// Fades out rather than cutting — a hard stop at the wrong moment wakes
// the person the timer was set for. Two seconds of ramp, then pause and
// restore the volume so the next session is not mysteriously silent.
let sleepHandle = null;

export function setSleepTimer(minutes) {
  if (sleepHandle) { clearTimeout(sleepHandle); sleepHandle = null; }
  if (!minutes) { setState({ sleepAt: null }); return; }
  const at = Date.now() + minutes * 60000;
  setState({ sleepAt: at });
  sleepHandle = setTimeout(() => {
    const restore = state.volume;
    const step = restore / 20;
    const fade = setInterval(() => {
      const v = Math.max(0, (el?.volume ?? 0) - step);
      if (el) el.volume = v;
      if (v <= 0.001) {
        clearInterval(fade);
        pause();
        if (el) el.volume = restore;
        setState({ sleepAt: null });
      }
    }, 100);
    sleepHandle = null;
  }, minutes * 60000);
}

// ════════════════════════════════════════════════════════════════════
// KEYBOARD — desktop courtesy, and free
// ════════════════════════════════════════════════════════════════════
// Space toggles, arrows seek, N/P move. Guarded against firing while
// someone is typing in a field, which is the one bug every implementation
// of this ships with: publishing a track called "My Song" should not
// pause the music twice on the way through.
export function attachKeyboard() {
  if (!hasDOM) return () => {};
  const onKey = (e) => {
    const t = e.target;
    if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
    if (!state.track) return;
    switch (e.key) {
      case " ": e.preventDefault(); toggle(); break;
      case "ArrowRight": seek((el?.currentTime || 0) + 5); break;
      case "ArrowLeft":  seek((el?.currentTime || 0) - 5); break;
      case "n": case "N": isBook(state.track) ? nextChapter() : next("user"); break;
      case "p": case "P": isBook(state.track) ? prevChapter() : prev(); break;
      default: break;
    }
  };
  window.addEventListener("keydown", onKey);
  return () => window.removeEventListener("keydown", onKey);
}

// ════════════════════════════════════════════════════════════════════
// THE HOOK
// ════════════════════════════════════════════════════════════════════
export function useAudio() {
  const [snap, setSnap] = useState(state);
  useEffect(() => {
    subs.add(setSnap);
    setSnap(state);                 // resync — the engine may have moved
    return () => { subs.delete(setSnap); };
  }, []);
  return snap;
}

export default useAudio;
